import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { SOCIAL_MEDIA_LINKS, buildWhatsAppUrl } from '../config/contactConfig';
import { APP_VERSION } from '../config/appConfig';

const Footer = () => {
  const { isRTL } = useLanguage();
  const contactRowClass = `text-gray-300 inline-flex items-center w-fit max-w-full gap-3 ${
    isRTL
      ? 'justify-end text-right'
      : 'justify-start text-left'
  }`;
  const contactIconClass = 'text-blue-400 w-6 text-center shrink-0';
  const contactTextClass = `min-w-0 ${isRTL ? 'text-right' : 'text-left'}`;
  const contactLinkClass = `${contactRowClass} hover:text-blue-400 transition-all duration-300 hover:scale-105`;
  const socialLabelById = {
    linkedin: isRTL ? 'لينكد إن' : 'LinkedIn',
    x: 'X',
    instagram: isRTL ? 'إنستغرام' : 'Instagram',
    tiktok: isRTL ? 'تيك توك' : 'TikTok',
    facebook: isRTL ? 'فيسبوك' : 'Facebook'
  };

  const renderContactContent = (link) => {
    const icon = <i className={`${link.icon} ${contactIconClass}`} aria-hidden="true"></i>;
    const text = (
      <span className={contactTextClass}>
        {link.label ? (
          <>
          <span dir="ltr" className="inline-block">{link.text}</span>
            <span>{link.label}</span>
            {' '}
            
          </>
        ) : (
          link.text
        )}
      </span>
    );

    return isRTL ? (
      <>
        {text}
        {icon}
      </>
    ) : (
      <>
        {icon}
        {text}
      </>
    );
  };

  const footerData = {
    connect: {
      title: isRTL ? 'تواصل معنا' : 'Connect',
      links: [
        {
          label: isRTL ? ' :البريد الإلكتروني' : null,
          text: 'info@jelaniai.com',
          href: 'mailto:info@jelaniai.com',
          icon: 'fas fa-envelope'
        },
        {
          text: isRTL ? 'دبي، الإمارات العربية المتحدة' : 'Dubai, UAE',
          href: null,
          icon: 'fas fa-map-marker-alt'
        },
        {
          text: isRTL ? 'واتساب' : 'WhatsApp',
          href: buildWhatsAppUrl(),
          icon: 'fab fa-whatsapp'
        }
      ]
    }
  };

  return (
    <footer className={`bg-gradient-to-r from-gray-900 to-gray-800 text-white pt-16 pb-8 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-12 max-w-4xl">
          {/* Connect Section */}
          <section
            aria-labelledby="footer-connect-heading"
            className={`${isRTL ? 'text-right md:justify-self-end' : 'text-left md:justify-self-start'} w-full max-w-md`}
          >
            <h3 id="footer-connect-heading" className="text-lg font-semibold mb-6 text-blue-400 border-b border-blue-600 pb-2 inline-block">
              {footerData.connect.title}
            </h3>
            <ul className="space-y-3">
              {footerData.connect.links.map((link, index) => (
                <li key={index}>
                  {link.href ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      dir="ltr"
                      className={contactLinkClass}
                    >
                      {renderContactContent(link)}
                    </a>
                  ) : (
                    <span
                      dir="ltr"
                      className={contactRowClass}
                    >
                      {renderContactContent(link)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>

          <section
            aria-labelledby="footer-social-heading"
            className={`${isRTL ? 'text-right md:justify-self-start' : 'text-left md:justify-self-end'} w-full max-w-md`}
          >
            <h3 id="footer-social-heading" className="text-lg font-semibold mb-6 text-blue-400 border-b border-blue-600 pb-2 inline-block">
              {isRTL ? 'تابعنا على' : 'Find us on'}
            </h3>
            <ul className="space-y-3">
              {SOCIAL_MEDIA_LINKS.map((link) => (
                <li key={link.id}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={link.label}
                    title={link.label}
                    dir={isRTL ? 'ltr' : undefined}
                    className={contactLinkClass}
                  >
                    {isRTL ? (
                      <>
                        <span dir="rtl" className={contactTextClass}>{socialLabelById[link.id] || link.label}</span>
                        <i className={`${link.icon} ${contactIconClass}`} aria-hidden="true"></i>
                      </>
                    ) : (
                      <>
                        <i className={`${link.icon} ${contactIconClass}`} aria-hidden="true"></i>
                        <span className={contactTextClass}>{socialLabelById[link.id] || link.label}</span>
                      </>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        </div>
        {/* Bottom Section */}
        <div className="border-t border-gray-700 pt-8">
          <div className={`flex flex-col md:flex-row justify-between items-center ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            {/* Logo and Brand */}
            <div dir={isRTL ? 'rtl' : 'ltr'} className="flex items-center gap-4 mb-4 md:mb-0">
              <img src="/logo.jpeg" alt="Jelani AI" className="w-12 h-12 rounded-full object-cover shadow-lg" />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                {isRTL ? 'الجيلاني الذكي' : 'Jelani AI'}
              </span>
            </div>

            {/* Copyright and Tagline */}
            <div className={`text-gray-400 text-sm max-w-md ${isRTL ? 'text-right' : 'text-left'}`}>
              <div className="mb-2">
                {isRTL ? 
                  '© 2024 الجيلاني الذكي. جميع الحقوق محفوظة.' : 
                  '© 2024 Jelani AI. All rights reserved.'
                }
              </div>
              <div className="text-xs text-gray-500">
                {isRTL ? 
                  'أول منصة ذكاء اصطناعي قانونية متخصصة في عقود البناء والهندسة في الشرق الأوسط' : 
                  'The First AI Legal Platform Specializing in Middle Eastern Construction & Engineering Contracts'
                }
              </div>
            </div>
          </div>

          {/* Additional Footer Elements */}
          <div className={`mt-8 pt-6 border-t border-gray-800 flex justify-center md:justify-end ${isRTL ? 'md:justify-start' : ''}`}>
            <div className={`text-xs text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
              <span>
                {isRTL ?
                  'مدعوم بتقنية الذكاء الاصطناعي المتقدمة | مطابق لمعايير FIDIC' :
                  'Powered by Advanced AI Technology | FIDIC Compliant'
                }
              </span>
              <span className={isRTL ? 'mr-2' : 'ml-2'}>
                {isRTL ? `الإصدار ${APP_VERSION}` : `v${APP_VERSION}`}
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
